import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Activity, CheckCircle, Clock, Zap, Target, Trophy } from 'lucide-react';
import { useGameSounds } from '../../../core/hooks/useGameSounds';
import TacticalOverlay from '../../../components/shared/TacticalOverlay';
import { chronosBank } from '../../../data/FocusTimeBank';

export default function ChronosEngineGame({ gameData, onComplete, onBack }) {
  const { playSuccess, playError, playExtinguish, playWin } = useGameSounds();
  const { id = 'game_chronos_engine', preGameBriefing } = gameData || {};

  // UX Calibration: Randomize 6 deep-work disruption scenarios per session
  const deckRef = useRef([]);

  if (deckRef.current.length === 0) {
    let array = [...chronosBank];
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    deckRef.current = array.slice(0, 6);
  }

  const activeDeck = deckRef.current;

  // --- STATE ---
  const [stage, setStage] = useState('briefing');
  const [typingText, setTypingText] = useState('');
  const [activeDistractions, setActiveDistractions] = useState([]);
  
  const [isFrozen, setIsFrozen] = useState(false);
  const [penaltyReason, setPenaltyReason] = useState(null);
  const [score, setScore] = useState(0);

  // --- ENGINE SENSORS & REFS ---
  const spawnCountRef = useRef(0);
  const spawnTimesRef = useRef({});
  const pendingLogsRef = useRef([]); 
  const [sessionAttempts, setSessionAttempts] = useState([]);
  const [timeToNextSpawn, setTimeToNextSpawn] = useState(5); // Initial spawn at 5s to hook engagement

  // 1. Briefing Mount
  useEffect(() => {
    if (preGameBriefing) {
      setStage('briefing');
    } else {
      setStage('playing');
    }
  }, [preGameBriefing]);

  // 2. The Asynchronous Chronos Triage Loop
  useEffect(() => {
    if (stage !== 'playing') return;

    const interval = setInterval(() => {
      if (spawnCountRef.current >= 6) {
        clearInterval(interval);
        return;
      }

      setTimeToNextSpawn(prev => {
        if (prev <= 1) {
          // Trigger the next distraction
          const nextAlert = activeDeck[spawnCountRef.current];
          if (nextAlert) {
            spawnTimesRef.current[nextAlert.id] = Date.now();
            setActiveDistractions(curr => [...curr, nextAlert]);
            spawnCountRef.current += 1;
          }
          // Reset interval to random 15 - 25 seconds for the next event
          return Math.floor(Math.random() * 11) + 15; 
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [stage, activeDeck]);

  // 3. Triage Validation & Telemetry Capture
  const handleTriage = (alert, choice) => {
    const isCorrect = choice === alert.optimalTriage;
    
    // SENSOR: Distraction Capture Rate (Latency between alert spawn and triage click)
    const captureRateMs = Date.now() - spawnTimesRef.current[alert.id];
    
    if (isCorrect) {
      playSuccess();
      setScore(s => s + 1);
    } else {
      playError();
      setIsFrozen(true);
      setPenaltyReason(alert.rationale);
      setTimeout(() => {
        setIsFrozen(false);
        setPenaltyReason(null);
      }, 3000);
    }

    setActiveDistractions(curr => curr.filter(d => d.id !== alert.id));

    // Queue the telemetry record. We wait for the next keystroke to calculate K_i.
    pendingLogsRef.current.push({
      scenarioId: alert.id,
      selectedTriage: choice,
      optimalTriage: alert.optimalTriage,
      isCorrect,
      captureRateMs,
      triageTimestamp: Date.now()
    });
  };

  // 4. Keyboard Resume Latency (K_i) Calculation
  const handleTyping = (e) => {
    if (isFrozen) return;
    setTypingText(e.target.value);

    // SENSOR: Capture K_i for all pending triages based on this exact keystroke
    if (pendingLogsRef.current.length > 0) {
      const now = Date.now();
      const finalizedLogs = pendingLogsRef.current.map(log => ({
        ...log,
        keyboardResumeLatencyMs: now - log.triageTimestamp
      }));
      
      // Strip temporary timestamp before saving to payload state
      const cleanedLogs = finalizedLogs.map(({ triageTimestamp, ...rest }) => rest);
      setSessionAttempts(prev => [...prev, ...cleanedLogs]);
      pendingLogsRef.current = [];
    }
  };

  const isCompleteReady = spawnCountRef.current >= 6 && activeDistractions.length === 0;

  const handleFinalSubmit = () => {
    if (!isCompleteReady) return;

    // Gracefully handle any final triages that never received a subsequent keystroke
    if (pendingLogsRef.current.length > 0) {
      const now = Date.now();
      const finalizedLogs = pendingLogsRef.current.map(log => ({
        ...log,
        keyboardResumeLatencyMs: now - log.triageTimestamp 
      }));
      const cleanedLogs = finalizedLogs.map(({ triageTimestamp, ...rest }) => rest);
      setSessionAttempts(prev => [...prev, ...cleanedLogs]);
      pendingLogsRef.current = [];
    }

    playWin();
    setStage('results');
  };

  const executeFinalPayload = () => {
    const payload = {
      id,
      rawInteractions: sessionAttempts
    };
    if (typeof onComplete === 'function') onComplete(payload);
  };

  // --- RESULTS RENDER ---
  if (stage === 'results') {
    const percentage = Math.round((score / 6) * 100);
    const passed = percentage >= 80;

    return (
      <div className="h-full flex flex-col items-center justify-center bg-slate-50 rounded-xl p-8 text-center animate-fade-in">
        <div className={`mb-6 p-6 rounded-full ${passed ? 'bg-emerald-100' : 'bg-red-100'}`}>
          {passed ? <Trophy className={`w-16 h-16 text-emerald-600`} /> : <Lock className="w-16 h-16 text-red-600" />}
        </div>
        <h2 className="text-3xl font-black text-gray-900 mb-2 uppercase tracking-widest">
          {passed ? "Deep Work Verified" : "Focus Shattered"}
        </h2>
        <p className="text-gray-600 mb-8 max-w-md">
          You successfully triaged <span className="font-bold">{score}/6</span> external distractions. Your <strong>Keyboard Resume Latency ($K_i$)</strong> metrics have been logged to the overarching telemetry engine.
        </p>
        <button
          onClick={executeFinalPayload}
          className="w-full max-w-xs bg-slate-900 text-white font-black py-4 px-6 rounded-xl shadow-lg hover:bg-black transition transform hover:scale-105 flex items-center justify-center uppercase tracking-widest"
        >
          <CheckCircle className="w-5 h-5 mr-2" /> Complete Audit
        </button>
      </div>
    );
  }

  // --- PLAYING RENDER (Split-Screen) ---
  return (
    <div className="flex flex-col h-full min-h-[600px] overflow-hidden rounded-xl border border-slate-200 bg-slate-50 font-sans relative">
      
      {/* OVERLAY: BRIEFING */}
      <AnimatePresence>
        {stage === 'briefing' && preGameBriefing && (
          <div className="absolute inset-0 z-[100]">
            <TacticalOverlay
              type="briefing"
              data={preGameBriefing}
              onContinue={() => setStage('playing')}
            />
          </div>
        )}
      </AnimatePresence>

      <div className="flex flex-1 overflow-hidden">
        {/* LEFT PANE: The Primary Task (Deep Work Editor) */}
        <div className="flex-1 flex flex-col relative p-6 md:p-10 bg-white shadow-inner z-10">
          <div className="border-b border-slate-100 pb-4 mb-6 flex justify-between items-end">
            <div>
              <h2 className="text-2xl font-black text-slate-800 tracking-tight">Q3 Strategic Risk Assessment</h2>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Deep Work Block</p>
            </div>
            <span className="text-xs font-bold text-indigo-500 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-100">
              Word Count: {typingText.split(/\s+/).filter(w => w.length > 0).length}
            </span>
          </div>

          <textarea
            className={`flex-1 w-full resize-none outline-none rounded-xl text-slate-800 text-lg leading-relaxed transition-all p-4 
              ${isFrozen ? 'bg-red-50/50 cursor-not-allowed border-2 border-red-500' : 'bg-slate-50 border border-slate-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-400/20'}`}
            disabled={isFrozen}
            value={typingText}
            onChange={handleTyping}
            placeholder="Begin drafting your strategic analysis here. The Q3 revenue goals rely on this report. Ignore unhelpful organizational noise and maintain your typing flow..."
          />

          <button
            disabled={!isCompleteReady}
            onClick={handleFinalSubmit}
            className={`mt-6 w-full py-4 rounded-xl font-black uppercase tracking-widest transition-all ${
              isCompleteReady 
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-600/30 cursor-pointer' 
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            {isCompleteReady ? 'Finalize & Submit Report' : `Enduring Focus Sprint... (${spawnCountRef.current}/6 Alerts)`}
          </button>

          {/* Micro-Penalty Freeze Overlay */}
          <AnimatePresence>
            {isFrozen && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="absolute inset-0 z-50 bg-red-500/10 backdrop-blur-[2px] flex flex-col items-center justify-center p-6 text-center"
              >
                <div className="bg-red-600 text-white p-8 rounded-2xl shadow-2xl max-w-md border-4 border-red-400 transform -rotate-1">
                  <Lock className="w-12 h-12 mx-auto mb-4" />
                  <h3 className="text-xl font-black uppercase tracking-widest mb-2">Cognitive Penalty</h3>
                  <p className="font-medium text-sm leading-relaxed">{penaltyReason}</p>
                  <div className="mt-6 text-[10px] font-black text-red-200 uppercase tracking-widest animate-pulse">
                    Keyboard Locked (3s)
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* RIGHT PANE: The Distraction Feed */}
        <div className="w-80 bg-slate-100 border-l border-slate-200 flex flex-col z-20 shadow-xl hidden md:flex">
          <div className="p-4 bg-slate-200 border-b border-slate-300 text-[10px] font-black text-slate-500 uppercase tracking-widest flex justify-between items-center">
            <span>Organizational Feed</span>
            <div className="flex items-center gap-1.5">
              <Activity size={12} className="text-indigo-500 animate-pulse" /> Live
            </div>
          </div>
          
          <div className="p-4 flex-1 overflow-y-auto space-y-4 custom-scrollbar">
            <AnimatePresence>
              {activeDistractions.map(alert => (
                <motion.div
                  key={alert.id}
                  initial={{ x: 100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-white p-4 rounded-xl shadow-md border border-slate-200 border-l-4 border-l-indigo-500 flex flex-col gap-3 relative overflow-hidden"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Target size={14} className="text-indigo-500" />
                    <span className="text-[10px] font-black uppercase tracking-wider text-indigo-600 truncate">
                      {alert.distractionSource}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-slate-800 leading-snug">{alert.messageText}</p>
                  
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    <button onClick={() => handleTriage(alert, 'Immediate Action')} className="text-xs font-bold py-2 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg transition-colors border border-red-100">
                      Immediate Action
                    </button>
                    <button onClick={() => handleTriage(alert, 'Schedule/Buffer')} className="text-xs font-bold py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors border border-blue-100">
                      Schedule / Buffer
                    </button>
                    <button onClick={() => handleTriage(alert, 'Ignore/Mute')} className="text-xs font-bold py-2 bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors border border-slate-200">
                      Ignore / Mute
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>
      
      {/* Mobile Feed Warning (Engine designed for Desktop resolution) */}
      <div className="md:hidden absolute inset-0 bg-slate-900 z-[200] flex flex-col items-center justify-center p-8 text-center">
         <Zap className="text-indigo-500 w-16 h-16 mb-4" />
         <h3 className="text-white font-black text-xl mb-2">Desktop Required</h3>
         <p className="text-slate-400 text-sm">The Split-Screen Chronos Engine requires a larger viewport to accurately measure peripheral cognitive triage. Please rotate your device or switch to a desktop.</p>
         <button onClick={onBack} className="mt-8 px-6 py-3 bg-slate-800 text-white font-bold rounded-xl">Exit Module</button>
      </div>

    </div>
  );
}